import React, { Suspense, useEffect, useMemo } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";

import { Auth0ProviderWithNavigate } from "@providers/auth0";
import { ThemeProvider } from "@providers/themeProvider";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import { useAuth0 } from "@auth0/auth0-react";
import { AuthenticationGuard } from "@components/authGuard";
import Navbar from "@components/navigation";
import { ReadOnlyModeProvider } from "@components/readOnlyModeProvider";
import { UnsavedChangesProvider } from "@providers/unsavedChanges";
import SuspenseLoader from "@components/suspenseLoader";
import ResearchAgent from "@pages/admin/researchAgent";
import Login from "@pages/auth/login";
import Logout from "@pages/auth/logout";
import PetitionBuilderHome from "@pages/builder/home";
import PetitionCreator from "@pages/create";
import Draft from "@pages/draft";
import ExhibitRepo from "@pages/exhibitRepo";
import RelocatedPage from "@pages/relocated";
import PostgresApolloProvider from "@providers/apollo";
import { CaptureConsole } from "@sentry/integrations";
import * as Sentry from "@sentry/react";
import { StatsigProvider } from "@statsig/react-bindings";
import { runStatsigSessionReplay } from "@statsig/session-replay";
import { useLogError } from "@utils/error";
import { useContact } from "@utils/hooks";
import { Toaster } from "react-hot-toast";
import { useAsyncClient } from "./vendors/statsig";

const Builder = React.lazy(() => import("@pages/builder"));
const LetterGenerator = React.lazy(() => import("@pages/admin/letterGen"));
const LetterGeneratorEndpoints = React.lazy(
  () => import("@pages/admin/letterGenEndpoints")
);
const Entities = React.lazy(() => import("@pages/entities"));

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://43a516e96c9fa93f374e1d1df0360176@o4506147990994944.ingest.sentry.io/4506152682651648",
    integrations: [
      new CaptureConsole({
        levels: ["error"],
      }),
      new Sentry.BrowserTracing({
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
          // "localhost",
          /^https:\/\/portal\.plymouthstreet\.org/,
          /^https:\/\/portal\.plymouthstreet\.com/,
          /^https:\/\/portal-ui\.onrender\.com/,
        ],
        // routingInstrumentation: Sentry.reactRouterV6Instrumentation(
        //   React.useEffect,
        //   useLocation,
        //   useNavigationType,
        //   createRoutesFromChildren,
        //   matchRoutes
        // ),
      }),
      new Sentry.Replay({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: false,
        networkDetailAllowUrls: [
          window.location.origin,
          "https://api.baseql.com",
        ],
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0, // This sets the sample rate at 100%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const USER_NODE_ENV = import.meta.env.VITE_NODE_ENV;

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const MainRoutes = () => {
  const location = useLocation();
  const { user } = useAuth0();
  const { admin } = useContact();
  const { statsigClient } = useAsyncClient();
  const logError = useLogError();
  useEffect(() => {
    if (!statsigClient) return;
    if (USER_NODE_ENV === "production" && admin === false) {
      try {
        runStatsigSessionReplay(statsigClient);
      } catch (e) {
        logError(e, {
          message: "Error initializing session replay",
        });
      }
    }
  }, [statsigClient]);

  const navbarVisible = useMemo(() => {
    const navbarVisiblePaths = [
      "home",
      "actions",
      "docs",
      "help",
      "track",
      "onboarding",
      "welcome",
    ];

    const page = location.pathname.split("/")[1];
    if (page === "") {
      return true;
    }

    return navbarVisiblePaths.includes(page);
  }, [location.pathname]);

  useEffect(() => {
    if (user == null) return;

    Sentry.setUser({
      id: user.sub,
      name: user.name,
      email: user.email,
    });
  }, [user]);

  return (
    <StatsigProvider
      loadingComponent={<SuspenseLoader />}
      client={statsigClient!}
    >
      <div className="min-h-screen h-full flex flex-row overflow-x-hidden">
        {navbarVisible && (
          <div>
            <Navbar />
          </div>
        )}
        <Toaster position="bottom-right" />
        <div className="w-full h-full">
          <ReadOnlyModeProvider>
            <UnsavedChangesProvider>
            <Routes>
              <Route path="/login" Component={Login} />
              <Route path="/logout" Component={Logout} />
              <Route
                path="/admin"
                element={<Navigate to="/admin/petition-builder" replace />}
              />
              <Route
                path="/admin/*"
                element={<Navigate to="/admin/petition-builder" replace />}
              />
              <Route
                path="/admin/researchagent"
                element={<AuthenticationGuard component={ResearchAgent} />}
              />
              <Route
                path="/admin/new_lettergen"
                element={
                  <Suspense fallback={<SuspenseLoader />}>
                    <AuthenticationGuard component={LetterGenerator} />
                  </Suspense>
                }
              />
              <Route
                path="/admin/lettergen"
                element={
                  <Suspense fallback={<SuspenseLoader />}>
                    <AuthenticationGuard component={LetterGeneratorEndpoints} />
                  </Suspense>
                }
              />
              <Route
                path="/admin/petition-builder"
                element={
                  <Suspense fallback={<SuspenseLoader />}>
                    <AuthenticationGuard component={PetitionBuilderHome} />
                  </Suspense>
                }
              />
              <Route
                path="/admin/petition-builder/new"
                element={
                  <Suspense fallback={<SuspenseLoader />}>
                    <AuthenticationGuard component={PetitionCreator} />
                  </Suspense>
                }
              />
              <Route
                path="/admin/petition-builder/:id"
                element={
                  <Suspense fallback={<SuspenseLoader />}>
                    <AuthenticationGuard component={Builder} />
                  </Suspense>
                }
              />
              <Route
                path="/admin/entities"
                element={
                  <Suspense fallback={<SuspenseLoader />}>
                    <AuthenticationGuard component={Entities} />
                  </Suspense>
                }
              />
              <Route
                path="/admin/exhibit-repo"
                element={
                  <Suspense fallback={<SuspenseLoader />}>
                    <AuthenticationGuard component={ExhibitRepo} />
                  </Suspense>
                }
              />
              <Route
                path="/admin/draft"
                element={
                  <Suspense fallback={<SuspenseLoader />}>
                    <AuthenticationGuard component={Draft} />
                  </Suspense>
                }
              />
              <Route path="*" element={<RelocatedPage />} />
            </Routes>
            </UnsavedChangesProvider>
          </ReadOnlyModeProvider>
        </div>
      </div>
    </StatsigProvider>
  );
};

root.render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <BrowserRouter>
        {/* <SentryRoutes> */}
        <Auth0ProviderWithNavigate>
          <PostgresApolloProvider>
            <MainRoutes />
          </PostgresApolloProvider>
        </Auth0ProviderWithNavigate>
        {/* </SentryRoutes> */}
      </BrowserRouter>
    </ThemeProvider>
  </React.StrictMode>
);
